import { EllipsisOutlined } from '@ant-design/icons';
import { Dropdown, Menu } from 'antd';
import type { FC } from 'react';
import { useStore } from 'zustand';

import type { Interval } from 'app/utils/dayjs';
import {
  addDays,
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  isAfter,
  isSameDay,
  isSameISOWeek,
  isSunday,
  startOfDay,
  subDays,
} from 'app/utils/dayjs';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { inventoryStore } from '../../InventoryStore';
import type { DuplicationModeData } from '../../type';
import { DuplicationMode } from '../../type';

import './DuplicationMenu.scss';

type DuplicationDropDownContentProps = {
  currentRange: Interval;
  date: Date;
  tryDuplicate: (modeData: DuplicationModeData) => void;
  handleDuplicateHover: (day: Date, hoverMode: DuplicationModeData) => void;
  dropDuplicationHover: () => void;
};

const DuplicationDropDownContent = ({
  date,
  currentRange,
  tryDuplicate,
  handleDuplicateHover,
  dropDuplicationHover,
}: DuplicationDropDownContentProps) => {
  const disableDuplicateToEndOfWeek = () => {
    const isEndOfWeek = isSunday(date);
    const endOfWeekDay = endOfWeek(startOfDay(date), {
      weekStartsOn: 1,
    });

    const isCurrentWeekIncomplete =
      isAfter(endOfWeekDay, currentRange.end) &&
      !isSameDay(endOfWeekDay, currentRange.end);

    return isEndOfWeek || isCurrentWeekIncomplete;
  };

  const disableDuplicateToEndOfNextWeek = () => {
    const isInLastWeek = isSameISOWeek(date, currentRange.end);
    const endOfNextWeekDay = endOfWeek(addWeeks(startOfDay(date), 1), {
      weekStartsOn: 1,
    });

    const isNextWeekIncomplete =
      isAfter(endOfNextWeekDay, currentRange.end) &&
      !isSameDay(endOfNextWeekDay, currentRange.end);

    return isInLastWeek || isNextWeekIncomplete;
  };

  const disableDuplicateToEndOfPeriod = isSameDay(date, currentRange.end);

  const remainingRange = isSameDay(date, currentRange.end)
    ? undefined
    : {
        start: date,
        end: subDays(currentRange.end, 1),
      };

  const getNumberOfDaysLabel = (day: Date) => {
    const numberOfDays = eachDayOfInterval({
      start: date,
      end: addDays(day, 1),
    }).length;

    return numberOfDays === 2 ? '1 day' : `${numberOfDays - 1} days`;
  };

  return (
    <Menu
      items={[
        {
          key: 'duplicate-to-end-of-week',
          disabled: disableDuplicateToEndOfWeek(),
          onMouseEnter: () =>
            handleDuplicateHover(date, {
              mode: DuplicationMode.END_OF_WEEK,
            }),
          onMouseLeave: () => dropDuplicationHover(),
          onClick: () => tryDuplicate({ mode: DuplicationMode.END_OF_WEEK }),
          label: 'Duplicate until end of this week',
        },
        {
          key: 'duplicate-to-end-of-next-week',
          disabled: disableDuplicateToEndOfNextWeek(),
          onMouseEnter: () =>
            handleDuplicateHover(date, {
              mode: DuplicationMode.END_OF_NEXT_WEEK,
            }),
          onMouseLeave: () => dropDuplicationHover(),
          onClick: () =>
            tryDuplicate({ mode: DuplicationMode.END_OF_NEXT_WEEK }),
          label: 'Duplicate until end of next week',
        },
        {
          key: 'duplicate-to-end-of-period',
          disabled: disableDuplicateToEndOfPeriod,
          onMouseEnter: () =>
            handleDuplicateHover(date, {
              mode: DuplicationMode.END_OF_TABLE,
            }),
          onMouseLeave: () => dropDuplicationHover(),
          onClick: () => tryDuplicate({ mode: DuplicationMode.END_OF_TABLE }),
          label: `Duplicate until end of period (${format(
            currentRange.end,
            'ddd DD/MM'
          )})`,
        },
        {
          key: 'duplicate-to-end-of-period-submenu',
          disabled: disableDuplicateToEndOfPeriod,
          label: 'Duplicate until day of my choice',
          children: remainingRange
            ? eachDayOfInterval(remainingRange).map((day) => ({
                key: `duplicate-to-end-of-period-submenu-${day}`,
                onMouseEnter: () =>
                  handleDuplicateHover(date, {
                    mode: DuplicationMode.CUSTOM,
                    customDay: day,
                  }),
                onMouseLeave: () => dropDuplicationHover(),
                onClick: () =>
                  tryDuplicate({
                    mode: DuplicationMode.CUSTOM,
                    customDay: day,
                  }),
                label: `${getNumberOfDaysLabel(day)} (${format(
                  addDays(day, 1),
                  'ddd DD/MM'
                )})`,
              }))
            : [],
        },
      ]}
    />
  );
};

export const DuplicationMenu: FC<Props> = ({
  date,
  currentRange,
  tryDuplicate,
  handleDuplicateHover,
  dropDuplicationHover,
}) => {
  const trackOpen = (open: boolean) => {
    if (open) {
      amplitudeTrack('Click Line Duplication', {
        from: format(date, 'YYYY-MM-DD'),
      });
    }
  };

  const isEditMode = useStore(inventoryStore, (store) => store.isEditMode);

  if (!isEditMode) {
    return null;
  }

  return (
    <Dropdown
      dropdownRender={() => (
        <DuplicationDropDownContent
          date={date}
          currentRange={currentRange}
          tryDuplicate={tryDuplicate}
          handleDuplicateHover={handleDuplicateHover}
          dropDuplicationHover={dropDuplicationHover}
        />
      )}
      trigger={['click']}
      onOpenChange={trackOpen}
    >
      <EllipsisOutlined rotate={90} className="duplication-menu-ellipsis" />
    </Dropdown>
  );
};

type Props = {
  currentRange: Interval;
  date: Date;
  tryDuplicate: (modeData: DuplicationModeData) => void;
  handleDuplicateHover: (day: Date, hoverMode: DuplicationModeData) => void;
  dropDuplicationHover: () => void;
};
